import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch, useSelector } from 'react-redux';
import { getUserDetails } from '../Stores/Actions/userAction';
import LoginPopup from './Login/LoginPopup';
import { HiMenu, HiX } from 'react-icons/hi';
import Button from './ui/Button';

const Mark = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <circle cx="14" cy="14" r="12.5" stroke="#75581F" strokeWidth="1.2" />
    <circle cx="14" cy="14" r="3.5" fill="#75581F" />
    <circle cx="14" cy="3" r="1.2" fill="#F6EFE2" />
    <circle cx="25" cy="14" r="1.2" fill="#F6EFE2" />
  </svg>
);

const Navbar = () => {
  const { tab } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const loginToken = localStorage.getItem('loginToken');
  const profileMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const [formData, setFormData] = useState({});
  const [openaccountPopup, setOpenaccountPopup] = useState(false);
  const [loginPopupOpen, setLoginPopupOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState(null);

  const api = axios.create({
    baseURL: `${process.env.REACT_APP_FRONTEND_URL}`,
  });

  api.interceptors.request.use(
    (config) => {
      if (loginToken) {
        config.headers.Authorization = `Bearer ${loginToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  useEffect(() => {
    const userData = async () => {
      try {
        const response = await api.post(`${process.env.REACT_APP_SERVER_URL}/getUser`);
        setFormData(response.data.data);
      } catch (error) {
        localStorage.clear();
        console.error(error);
      }
    };
    if (loginToken) {
      userData();
    }
  }, [loginToken]);

  useEffect(() => {
    dispatch(getUserDetails(formData));
  }, [dispatch, formData]);

  useEffect(() => {
    const firstPathSegment = location.pathname.replace(/^\//, '').split('/')[0];
    setActiveMenuItem(firstPathSegment || tab || 'Home');
  }, [location.pathname, tab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setOpenaccountPopup(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleMenuItemClick = (menuItem) => {
    setActiveMenuItem(menuItem);
    setMobileMenuOpen(false);
    setOpenaccountPopup(false);
    navigate(`/${menuItem}`);
  };

  const logOut = () => {
    toast('Logged out successfully', { type: 'success' });
    localStorage.clear();
    setFormData({});
    setOpenaccountPopup(false);
    setMobileMenuOpen(false);
    navigate('/');
  };

  const navItems = [
    { id: 'Home', label: 'Home', path: '' },
    { id: 'book', label: 'Book Session', path: 'book' },
    { id: 'services', label: 'Services', path: 'services' },
    { id: 'blog', label: 'Blog', path: 'blog' },
    { id: 'seminar', label: 'Seminar', path: 'seminar' },
    { id: 'media-profile', label: 'About', path: 'media-profile' },
    { id: 'session-history', label: 'My Sessions', path: 'session-history' },
    { id: 'contactUs', label: 'Contact', path: 'contactUs' },
  ];

  const isLoggedIn = formData && Object.keys(formData).length > 0;

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-canvas/10 bg-ground-deep">
        <nav className="mx-auto flex max-w-container items-center justify-between gap-4 px-5 py-4 sm:px-8 lg:px-10">
          <button type="button" className="flex items-center gap-2.5" onClick={() => navigate('/')}>
            <Mark />
            <span className="font-serif text-lg italic text-canvas">Bhavishyavani</span>
          </button>

          <ul className="hidden items-center gap-7 xl:flex">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => handleMenuItemClick(item.path)}
                  className={`text-[11px] font-medium uppercase tracking-[0.2em] transition ${
                    activeMenuItem === item.id ? 'text-canvas' : 'text-canvas/55 hover:text-canvas'
                  }`}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="primary"
              className="hidden !px-5 !py-2.5 xl:inline-flex"
              onClick={() => setOpenaccountPopup(!openaccountPopup)}
            >
              {isLoggedIn ? 'My Profile' : 'Sign In'}
            </Button>

            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-canvas/20 text-canvas xl:hidden"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <HiX size={20} /> : <HiMenu size={20} />}
            </button>
          </div>
        </nav>
      </header>

      {mobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="fixed inset-0 top-[65px] z-[60] overflow-y-auto bg-ground-deep px-6 py-8 xl:hidden"
        >
          <div className="mx-auto flex max-w-container flex-col gap-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleMenuItemClick(item.path)}
                className={`w-full rounded-none px-4 py-3.5 text-left text-base font-medium transition ${
                  activeMenuItem === item.id ? 'bg-canvas/10 text-canvas' : 'text-canvas/70 hover:bg-canvas/5'
                }`}
              >
                {item.label}
              </button>
            ))}

            <div className="mt-4 border-t border-canvas/10 pt-5">
              <p className="mb-3 px-1 text-[11px] font-medium uppercase tracking-[0.2em] text-canvas/50">
                Account
              </p>

              {isLoggedIn ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    className="w-full rounded-none bg-canvas/10 px-4 py-3 text-left text-sm font-semibold text-canvas"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/Account');
                    }}
                  >
                    My Account Details
                  </button>
                  <button
                    type="button"
                    className="w-full rounded-none bg-canvas/10 px-4 py-3 text-left text-sm font-semibold text-canvas"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/payments');
                    }}
                  >
                    My Payments
                  </button>
                  <button
                    type="button"
                    className="w-full rounded-none bg-canvas/10 px-4 py-3 text-left text-sm font-semibold text-canvas"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate('/support');
                    }}
                  >
                    Help &amp; Support
                  </button>
                  <button
                    type="button"
                    className="w-full rounded-none border border-canvas/15 px-4 py-3 text-left text-sm font-semibold text-canvas"
                    onClick={logOut}
                  >
                    Log Out
                  </button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setLoginPopupOpen(true);
                  }}
                >
                  Log In
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {openaccountPopup && (
        <div ref={profileMenuRef} className="fixed right-6 top-[72px] z-[60] hidden w-[min(92vw,280px)] xl:block">
          {isLoggedIn ? (
            <div className="rounded-none border border-canvas/10 bg-ground-deep p-5 shadow-elevation-md">
              <div className="mb-4 text-center text-[11px] font-medium uppercase tracking-[0.2em] text-canvas/50">
                My Profile
              </div>
              <ul className="space-y-2.5">
                <li>
                  <button
                    type="button"
                    className="w-full rounded-none bg-canvas/10 px-4 py-3 text-left text-sm font-semibold text-canvas hover:bg-canvas/15"
                    onClick={() => navigate('/Account')}
                  >
                    My Account Details
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="w-full rounded-none bg-canvas/10 px-4 py-3 text-left text-sm font-semibold text-canvas hover:bg-canvas/15"
                    onClick={() => navigate('/payments')}
                  >
                    My Payments
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="w-full rounded-none bg-canvas/10 px-4 py-3 text-left text-sm font-semibold text-canvas hover:bg-canvas/15"
                    onClick={() => navigate('/support')}
                  >
                    Help &amp; Support
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="w-full rounded-none border border-canvas/15 px-4 py-3 text-left text-sm font-semibold text-canvas hover:border-canvas/40"
                    onClick={logOut}
                  >
                    Log Out
                  </button>
                </li>
              </ul>
            </div>
          ) : (
            <div className="rounded-none border border-canvas/10 bg-ground-deep p-5 text-center shadow-elevation-md">
              <div className="mb-4 text-[11px] font-medium uppercase tracking-[0.2em] text-canvas/50">
                Welcome to Bhavishyavani
              </div>
              <Button type="button" variant="primary" className="w-full" onClick={() => setLoginPopupOpen(true)}>
                Log In
              </Button>
            </div>
          )}
        </div>
      )}

      <ToastContainer />
      {loginPopupOpen && <LoginPopup onClose={() => setLoginPopupOpen(false)} />}
    </>
  );
};

export default Navbar;
